import React, { useEffect, useState, useCallback } from 'react';
import { Search, ShoppingBag, Loader2, LogIn, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { gov } from '../../lib/govApi';
import { useGovCart } from '../../contexts/GovCartContext';
import { useAuth } from '../../contexts/AuthContext';
import { portalPath } from '../../lib/portal';
import { toast } from '../../hooks/use-toast';

const GovCatalog = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const { add } = useGovCart();

  const canOrder = !!user && ['purchaser', 'agency_admin', 'super_admin', 'admin'].includes(user.role);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        gov.catalog({ ...(category !== 'all' ? { category } : {}), ...(q.length >= 2 ? { q } : {}) }),
        gov.categories(),
      ]);
      setProducts(pRes.data || []);
      setCategories(cRes.data || []);
    } catch (e) {
      toast({ title: 'Catalog error', description: e.response?.data?.detail || 'Failed to load catalog', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [category, q]);

  useEffect(() => { fetchAll(); }, [category, fetchAll]);

  const onSearch = (e) => { e.preventDefault(); fetchAll(); };

  const onAdd = (p) => {
    add(p, 1);
    toast({ title: 'Added to cart', description: p.name });
  };

  return (
    <div className="space-y-5" data-testid="gov-catalog-page">
      {!user && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 flex items-center gap-3" data-testid="guest-view-banner">
          <Eye className="h-5 w-5 text-amber-700 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-900">You&apos;re browsing the DES catalog in read-only mode.</div>
            <div className="text-xs text-amber-800">Sign in with your authorised agency account to add items to a cart and submit a purchase order under Washington State Contract 09925.</div>
          </div>
          <Link to={portalPath('/login')}>
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="guest-signin-btn">
              <LogIn className="h-4 w-4 mr-1" /> Sign in to order
            </Button>
          </Link>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">DES Catalog</h1>
          <p className="text-sm text-slate-500">All prices reflect Washington State Contract 09925 awarded pricing.</p>
        </div>
        <form onSubmit={onSearch} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input className="pl-10 w-56" placeholder="Search by name or SKU" value={q} onChange={(e) => setQ(e.target.value)} data-testid="catalog-search-input" />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-48" data-testid="catalog-category-select"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(c => <SelectItem key={c.slug} value={c.slug}>{c.slug.replace(/-/g, ' ')} ({c.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" className="bg-blue-700 hover:bg-blue-800 text-white">Search</Button>
        </form>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-10 w-10 animate-spin text-blue-700" /></div>
      ) : products.length === 0 ? (
        <Card className="border-2 border-dashed">
          <CardContent className="p-10 text-center text-slate-500" data-testid="catalog-empty">No products match your search.</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {products.map(p => (
            <Card key={p.id} className="border-2 hover:shadow-lg transition" data-testid={`catalog-card-${p.id}`}>
              <CardContent className="p-4">
                <div className="aspect-square bg-slate-100 rounded mb-3 overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-semibold text-slate-800 text-sm line-clamp-2 min-h-[2.5rem]">{p.name}</h3>
                <div className="text-xs text-slate-500 mt-1">{p.manufacturer} · SKU {p.sku}</div>
                <div className="text-xs text-slate-400">Stock: {p.stock} {p.uom}</div>
                <div className="flex items-center justify-between mt-3">
                  <div>
                    <div className="text-lg font-bold text-blue-700">${Number(p.contract_price || p.price).toFixed(2)}</div>
                    <div className="text-[10px] uppercase tracking-wide text-emerald-700">DES Contract</div>
                  </div>
                  {canOrder ? (
                    <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" onClick={() => onAdd(p)} data-testid={`catalog-add-${p.id}`}>
                      <ShoppingBag className="h-4 w-4 mr-1" /> Add
                    </Button>
                  ) : (
                    <Link to={portalPath('/login')}>
                      <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-50" data-testid={`catalog-signin-${p.id}`}>
                        <LogIn className="h-4 w-4 mr-1" /> Sign in
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default GovCatalog;
